// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : components for vl.state
// =============================================================================
// ---Third-party imports
import React, {useState, useEffect} from 'react'
import {Dropdown, Image, Icon} from 'semantic-ui-react'

// ---Framework imports
import {
  dict,
  mapItems,
  has,
  sorted,
  items,
  size,
  isArray,
  isNil,
  get,
  filterItems,
  flatten,
} from 'ldash'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import connect from 'uix/connect'
import {translate} from 'uix/i18n'
import {Graphics} from 'uix/graphics'
import {useMetadataDBFields} from 'uix/db/hooks'
import {useActions, useCallback, useSelector} from 'uix/hooks'

// ---Local imports
import {translateState} from './'
import {loadStatesConfig} from './actions'
import {XMenu} from 'uix/components'

// ---Exports

export const getSortedStates = (dstates, statusType) =>
  sorted(items(dstates[statusType]), e => parseInt(e[1].value))

const getEnabledStates = (dstates, statusType, statusValue) => {
  /* return enabled state names
  - either by checking mode=manual/automatic
  - or evaluation of nextstate/startstate in mode=statemachine
   */
  let states = dstates[statusType]
  let byValue = dict(mapItems(states, (name, state) => [state.value, {...state, name}]))

  // prepare for mode statemachine
  let allowedNextStatemachine = null
  if (!isNil(statusValue)) {
    if (!isArray(statusValue)) {
      statusValue = [statusValue]
    }
    // evaluate nextStates for each statusValue
    let nextStates = statusValue
      .filter(v => has(byValue, v)) // XXX: seems to happen in zipcapsule states
      .map(v => byValue[v].mode == 'statemachine' && byValue[v].nextstate)
      .filter(e => e)
      .map(e => new Set(...e))
    if (nextStates.length > 0) {
      // now build intersection of all nextstates
      let nextStateValues = [...nextStates[0]].filter(v =>
        nextStates.slice(1).every(other => other.has(v))
      )
      allowedNextStatemachine = nextStateValues
        .filter(v => has(byValue, v))
        .map(v => byValue[v].name)
    }
  }

  let isEnabled = (name, state) => {
    if (state.mode == 'manual') {
      return true
    } else if (state.mode == 'automatic') {
      return false
    } else if (state.mode == 'statemachine') {
      if (isNil(statusValue)) {
        // startstate=manual in states.xml
        if (state.startstate) {
          return true
        }
      } else {
        // check nextstate rules
        return !!allowedNextStatemachine?.includes(name)
      }

      return false
    }
    return true
  }

  return mapItems(states, (name, state) => name, isEnabled)
}

export const configLoaded = (dname, dstates, loadStatesConfig) => {
  useEffect(() => {
    if (isNil(dstates)) {
      //~ log(states, !states[dname])
      loadStatesConfig(dname)
    }
  }, [dname])
  return !isNil(dstates)
}

export const statesCompProps = {
  storeMap: (root, props) => ({
    i18n: Sel.i18n(root),
    dstates: Sel.states(root),
    dname: root.meta.request.dname,
  }),
  actionMap: {loadStatesConfig},
}

export let StateDropdown = connect(
  'StateDropdown',
  statesCompProps,
  ({
    statusType,
    dname,
    dstates,
    loadStatesConfig,
    i18n,
    onClick,
    statusValue,
    allowReset = false,
  }) => {
    let label = i18n.get('stateChange')
    if (!configLoaded(dname, dstates, loadStatesConfig)) {
      return <Dropdown text={label} loading />
    }

    let icon = null

    if (!isNil(statusValue)) {
      // ugly
      let statusName = null
      let l = filterItems(
        dstates[statusType],
        (name, state) => String(state.value) === String(statusValue)
      )
      if (l.length > 0) {
        label = translateState(i18n, statusType, l[0][1].value)
        statusName = l[0][0]
      }
      if (Graphics.states) {
        icon = <StateIcon type={statusType} verticalAlign="sub" name={statusName} width={16} />
      }
    }

    let trigger = (
      <>
        {icon} {label}
      </>
    )

    if (!onClick) {
      onClick = data => log.info('implement onClick on StateDropdown')
    }

    let enabledStates = getEnabledStates(dstates, statusType, statusValue)
    //~ enabledStates = []

    let sortedStates = getSortedStates(dstates, statusType)
    let dditems = sortedStates.map(([name, state]) => (
      <Dropdown.Item
        key={name}
        disabled={!enabledStates.includes(name)}
        onClick={(e, data) =>
          onClick({
            name,
            value: state.value,
            label: translateState(i18n, statusType, state.value),
          })
        }
      >
        {<StateIcon type={statusType} name={name} />}
        {translateState(i18n, statusType, state.value)}
      </Dropdown.Item>
    ))
    if (allowReset && dditems.length > 0) {
      dditems.push(
        <Dropdown.Item
          key="__NONE_STATE__"
          icon="close"
          text="Status zurücksetzen"
          onClick={(e, data) =>
            onClick({name: '__NONE_STATE__', value: null, label: 'Status zurücksetzen'})
          }
        />
      )
    }

    return (
      <XMenu.Dropdown trigger={trigger} disabled={dditems.length == 0}>
        <Dropdown.Menu>{dditems}</Dropdown.Menu>
      </XMenu.Dropdown>
    )
  }
)

export const NodeStatesDropdown = ({node, statusType}) => {
  let res = useMetadataDBFields(node.otid, ['qs.value'])
  let setStateMany = useActions(Actions.setStateMany)
  let onClick = useCallback(({name}) => setStateMany(node.otid, statusType, name), [
    node.otid,
    statusType,
  ])
  return <StateDropdown statusType={statusType} statusValue={res['qs.value']} onClick={onClick} />
}

export const StateIcon = ({type, name = null, statusValue, verticalAlign, width = 20, title}) => {
  /* render state icon given type + name (filename)
   */
  if (statusValue) {
    // conditional selector is probably ok here
    let {byValue} = useSelector(Sel.domainStates)
    name = byValue[type]?.[statusValue]
    if (!name) {
      // log.warn('missing state mapping (no config loaded?)')
      return null
    }
  }

  let s = `${type}${name ? '/' : ''}${name || ''}`
  if (!Graphics.states) {
    return null
  }
  let data = Graphics.states[s]

  if (data) return <Image style={{width, verticalAlign}} inline src={data} title={title} />
  //~ log.warn('graphic not found: %o', s)
  return null
}

export const StateDescription = ({statusType, statusValue, icon = false}) => {
  /* render state translation given type + value (db field value)
   */
  let i18n = useSelector(Sel.i18n)
  let text = translateState(i18n, statusType, statusValue)

  return (
    <span>
      {icon && <StateIcon type={statusType} statusValue={statusValue} {...icon} />} {text}
    </span>
  )
}
